import { DEFAULT_DATE_LOCALE, type DateLocaleSettings } from '@/lib/companySettings';

/* ------------------------------------------------------------------ */
/* Shared date/time formatting -- timezone + date-format aware          */
/*                                                                      */
/* A module-level cache (not a React hook) so the ~100 existing call    */
/* sites across the app that format dates synchronously can just import */
/* formatDate/formatTime/formatDateTime without becoming async. The     */
/* cache is populated once at app bootstrap (AppContext.tsx's settings  */
/* load) and updated immediately whenever Company Settings saves a      */
/* change, so every screen reflects it without a page reload.           */
/*                                                                      */
/* Uses Intl.DateTimeFormat with an IANA time zone (e.g. 'Pacific/      */
/* Auckland'), which accounts for daylight saving automatically -- no   */
/* separate DST handling is needed.                                     */
/* ------------------------------------------------------------------ */

let cache: DateLocaleSettings = { ...DEFAULT_DATE_LOCALE };

export function setDateLocaleCache(settings: DateLocaleSettings): void {
  cache = settings;
}

export function getDateLocaleCache(): DateLocaleSettings {
  return cache;
}

export function getAppTimeZone(): string {
  return cache.timezone;
}

const toDate = (input: string | number | Date | null | undefined): Date | null => {
  if (input === null || input === undefined || input === '') return null;
  const d = input instanceof Date ? input : new Date(input);
  return isNaN(d.getTime()) ? null : d;
};

const getParts = (d: Date) => {
  const parts = new Intl.DateTimeFormat('en-NZ', {
    timeZone: cache.timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
    dayPeriod: get('dayPeriod').toLowerCase(),
  };
};

/** Formats a date using the configured time zone + date-format preference. */
export function formatDate(input: string | number | Date | null | undefined): string {
  const d = toDate(input);
  if (!d) return '';
  const { year, month, day } = getParts(d);
  switch (cache.dateFormat) {
    case 'MM/DD/YYYY':
      return `${month}/${day}/${year}`;
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`;
    case 'DD/MM/YYYY':
    default:
      return `${day}/${month}/${year}`;
  }
}

/** Formats the time-of-day portion using the configured time zone (e.g. "3:45 pm"). */
export function formatTime(input: string | number | Date | null | undefined): string {
  const d = toDate(input);
  if (!d) return '';
  const { hour, minute, dayPeriod } = getParts(d);
  return `${hour}:${minute} ${dayPeriod}`;
}

/** Formats date and time together using the configured time zone + date format. */
export function formatDateTime(input: string | number | Date | null | undefined): string {
  const d = toDate(input);
  if (!d) return '';
  return `${formatDate(d)}, ${formatTime(d)}`;
}
